// Spooky style instruments (docs/AUDIO_DESIGN.md §12, style 'spooky'): an old music box / celesta (additive bell tones with
// a twin tooth beating against each note, a bright inharmonic strike, the pin's click, a governor flutter on the bus, and a
// key wound before the tune), a soft upright bass, a slow kit of a deep kick, finger snaps, a tick-tock clock, a wooden
// knock and a bone rattle, and a theremin (or a bowed saw) that glides into every note.
// Every node goes in the voice registry: persistent parts tagged `${tag}:p`, per-note voices `${tag}`.
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { gain, filter, osc, panner, persistent } from '../instruments.js';

// Levels (tuning knobs for the mix balance against jazzhop's EP / bass / kit / lead).
export const SPOOKY = { note: 0.62, strike: 0.5, click: 0.25, wind: 0.4, box: 1, bass: 0.34, kit: 1.25, lead: 1.55 };

/** delay swing (s) that bends the pitch ±c cents at f Hz: (2^(c/1200) − 1) / (2πf) (the mixer's wow formula) */
const wowSec = (c, f) => (Math.pow(2, c / 1200) - 1) / (2 * Math.PI * f);
const cents = c => Math.pow(2, c / 1200);

/* ---------------- music box / celesta ---------------- */
// Per-context waves: the tone (the fundamental + .14 of the octave + .035 of the 3rd: a little body from the resonator and
// the wooden box), the twin tooth (one partial at .3) and the strike (partials at 2.76 f, 5.52 f and 8.28 f at 1 · .4 · .18:
// a free bar's 2nd mode and above, inharmonic against the fundamental).
const WAVES = new WeakMap();
function waves(ac) {
  let w = WAVES.get(ac);
  if (!w) {
    const pw = a => ac.createPeriodicWave(new Float32Array(a.length), Float32Array.from(a), { disableNormalization: true });
    WAVES.set(ac, (w = { tone: pw([0, 1, 0.14, 0.035]), twin: pw([0, 0.3]), strike: pw([0, 1, 0.4, 0.18]) }));
  }
  return w;
}
/** register panners: low −.2 · middle 0 · high +.2 */
const band3 = m => (m < 55 ? 0 : m < 70 ? 1 : 2);
/** level by register: the low teeth eased (×.8 at MIDI 40), the top eased above MIDI 76 (×.7 at 91) */
const regLvl = m => (m < 52 ? 0.8 + 0.2 * clamp((m - 40) / 12, 0, 1) : m > 76 ? Math.max(0.7, 1 - 0.02 * (m - 76)) : 1);

/**
 * createCelesta(ac, out, send, plan, ctx) → { chord, stats, dispose }. plan.spooky: { detune ¢, twin ¢, strike, click,
 * flutter ¢, rate Hz, roll, wind } · plan.ep.lp: the bus low-pass.
 * Per note (5 nodes, 4 in lite): the tone at f (±detune ¢: an old comb, a little out of tune), its twin tooth (.3, twin ¢
 * away: slow beating; not in lite) and the strike (at 2.76 f, S = .5·strike·v^1.1·clamp((f/330)^.6, .3, 1.3) over the note,
 * τ .16·(440/f)^.3 s: the bright "tink", dull on the low teeth) into one amp: 2.5 ms linear attack to .62·v^1.3, settling to
 * .6 of it (τ 80 ms), then the ring from +.22 s with τ 2·(262/f)^.55 s (0.6–3.5 s); the damper at the chord's end τ .2 s (or
 * the event's release: the final chord's .9 s). The final chord winds down: every note sags −30 ¢ from +.4 s (τ ≥ .35 × its
 * length).
 * Bus: 3 register panners → flutter (a DelayNode at 8 ms swept ±flutter ¢ at rate Hz: the spring governor) → peaking 700 Hz
 * +2 dB (the wooden box) → LP plan.ep.lp (Q .6) → ×SPOOKY.box → dry, reverb .4. The mechanism: one looped noise → BP 3.2 kHz
 * → a gate per note-on (.25·click·v, τ 3 ms: the pin plucking the tooth, ~30 dB under the note); with plan.spooky.wind the key
 * is wound as the tune starts (two turns of 6 ratchet clicks 60 ms apart from t0 + .15 s, .4 × .6–1, the BP at 1.4 kHz for
 * 1.4 s so the intro's closed tone LP lets them through: peaks ~26 dB under the first chord).
 * One pool of voices under ctx.cap: a tooth struck again damps its old note (30 ms); a steal takes a released note first.
 */
export function createCelesta(ac, out, send, plan, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null, stepDur = 0.25 }) {
  const P = plan.spooky, lite = cap < 16, W = waves(ac), nodes = [], src = [];
  const N = x => (nodes.push(x), x);
  const pans = [-0.2, 0, 0.2].map(p => N(panner(ac, p))), sum = N(gain(ac, 1));
  const dly = N(ac.createDelay(0.03)), lfo = N(osc(ac, 'sine', P.rate)), lfoG = N(gain(ac, wowSec(P.flutter, P.rate)));
  dly.delayTime.value = 0.008;
  const body = N(filter(ac, 'peaking', 700, 0.9, 2)), lp = N(filter(ac, 'lowpass', plan.ep.lp, 0.6));
  const outG = N(gain(ac, SPOOKY.box)), sendG = N(gain(ac, 0.4));
  for (const p of pans) p.connect(sum);
  sum.connect(dly); dly.connect(body); body.connect(lp); lp.connect(outG); outG.connect(out); outG.connect(sendG); sendG.connect(send);
  lfo.connect(lfoG); lfoG.connect(dly.delayTime); src.push(lfo);
  const cSrc = N(ac.createBufferSource()), cBP = N(filter(ac, 'bandpass', 3200, 1.1)), cG = N(gain(ac, 0));
  cSrc.buffer = noise(ac, 'white'); cSrc.loop = true;
  cSrc.connect(cBP); cBP.connect(cG); cG.connect(pans[1]); src.push(cSrc);
  const bus = persistent(registry, tag, src, nodes, t0, r() * 2);
  if (P.wind && t0 >= ac.currentTime) {               // winding the key: two turns of the ratchet, duller (the intro's tone
    cBP.frequency.setValueAtTime(1400, t0); cBP.frequency.setValueAtTime(3200, t0 + 1.4);   // LP opens from 900 Hz)
    for (let k = 0; k < 12; k++) {
      const tk = t0 + 0.15 + (k < 6 ? k : k + 4) * 0.06 + (r() - 0.5) * 0.012;
      cG.gain.setTargetAtTime(SPOOKY.wind * (0.6 + 0.4 * r()), tk, 0.0006); cG.gain.setTargetAtTime(0, tk + 0.0015, 0.005);
    }
  }

  let voices = [], maxActive = 0, steals = 0, notes = 0;      // { v, m, start, off, end, rec }
  /** fade a voice from t (τ); `now` ends it for the cap and the log at t (a steal), else after 4τ */
  const release = (x, t, tau, now = false) => {
    registry.stop(x.v, t, tau);
    x.off = Math.min(x.off, t);
    x.end = x.start >= t ? x.start : Math.min(x.end, now ? t : t + 4 * tau);
    if (x.rec) x.rec[1] = x.end;
  };
  const oldest = xs => xs.reduce((a, x) => (!a || x.start < a.start ? x : a), null);

  function note(t, m, vel, dur, relTau) {
    const v = clamp(vel, 0.05, 1), f = mtof(m), off = t + Math.max(0.05, dur);
    voices = voices.filter(x => x.end > t);
    for (const x of voices) if (x.m === m && x.start < t) release(x, t, 0.03);          // the same tooth struck again
    if (voices.length >= cap) {                     // steal: a released note first, then the oldest
      const x = oldest(voices.filter(y => y.off <= t)) || oldest(voices);
      release(x, t, 0.01, true);
      voices = voices.filter(y => y !== x);
      steals++;
    }
    const dA = (r() * 2 - 1) * P.detune;
    const o1 = osc(ac, 'sine', f), o3 = osc(ac, 'sine', 2.76 * f), g3 = gain(ac, 0), amp = gain(ac, 0), oscs = [o1, o3];
    o1.setPeriodicWave(W.tone); o3.setPeriodicWave(W.strike);
    o1.detune.value = dA; o3.detune.value = dA;
    o1.connect(amp); o3.connect(g3); g3.connect(amp); amp.connect(pans[band3(m)]);
    if (!lite) {
      const o2 = osc(ac, 'sine', f);
      o2.setPeriodicWave(W.twin); o2.detune.value = dA + P.twin; o2.connect(amp); oscs.push(o2);
    }
    const A = SPOOKY.note * Math.pow(v, 1.3) * regLvl(m), tA = t + 0.0025, tB = t + 0.22;
    const td = clamp(2 * Math.pow(262 / f, 0.55), 0.6, 3.5), tr = relTau >= 0.3 ? relTau : 0.2;
    const g = amp.gain;
    g.setValueAtTime(0, t); g.linearRampToValueAtTime(A, tA);
    g.setTargetAtTime(0.6 * A, tA, 0.08);
    if (off > tB) g.setTargetAtTime(0, tB, td);
    g.setTargetAtTime(0, off, tr);
    const S = SPOOKY.strike * P.strike * Math.pow(v, 1.1) * clamp(Math.pow(f / 330, 0.6), 0.3, 1.3);
    g3.gain.setValueAtTime(S, t); g3.gain.setTargetAtTime(0, tA, 0.16 * Math.pow(440 / f, 0.3));
    if (relTau >= 0.5) {                            // the final chord: the spring runs down
      for (const o of oscs) { const d = o.detune.value; o.detune.setValueAtTime(d, t + 0.4); o.detune.setTargetAtTime(d - 30, t + 0.4, Math.max(0.6, 0.35 * dur)); }
    }
    cG.gain.setTargetAtTime(SPOOKY.click * P.click * v, t, 0.0005); cG.gain.setTargetAtTime(0, t + 0.0012, 0.003);
    const stop = Math.min(off + 5 * tr, tB + 7 * td), end = Math.min(off + 4 * tr, tB + 4.5 * td);
    for (const o of oscs) { o.start(t); o.stop(stop); }
    const x = { v: registry.add({ src: oscs, all: [...oscs, g3, amp], start: t, end: stop, tag, out: amp }), m, start: t, off, end, rec: log ? [t, end] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }

  return {
    /** a chord (or the notes a comping cell picks), bottom to top over `strum` s; a block of 3+ notes is rolled over
     *  50–90 ms with p plan.spooky.roll. The top +10 % velocity, the low teeth (below MIDI 55) −12 %. */
    chord(t, midis, vel, dur, strum = 0.02, rel = 0.08) {
      const n = midis.length, off = t + dur;
      // the harmony moved on before earlier notes end (the engine's push): damp them now
      for (const x of voices) if (x.start < t - 0.03 && x.off > t + 0.05 && x.off < off - 2 * stepDur) release(x, t + 0.012, 0.2);
      const roll = n >= 3 && r() < P.roll ? 0.05 + r() * 0.04 : strum;
      midis.forEach((m, k) => {
        const dt = n > 1 ? (k * roll) / (n - 1) : 0;
        const vk = (n > 1 && k === n - 1 ? Math.min(1, vel * 1.1) : m < 55 ? vel * 0.88 : vel) * (0.95 + 0.1 * r());
        note(t + dt, m, vk, dur - dt, rel);
      });
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); },
  };
}

/* ---------------- upright bass: one oscillator through a plucked low-pass ---------------- */
/** Partials 1 · .45 · .2 · .1 · .05 · .025 (a soft, round upright) → LP (Q .7) that plucks open to 900 + 900v Hz and closes
 *  to 420 Hz (τ 110 ms) → amp: attack τ 5 ms to SPOOKY.bass·v, the pluck falling to .55 (τ .28 s), then the ring from +.5 s
 *  (τ 2.6 s: a held root sustains like a sub); release τ 70 ms. A slide (a note starting on the last one's end) glides there (τ 30 ms) without a new pluck. */
export function createUpright(ac, out, { registry, tag, t0 = ac.currentTime }) {
  const o = osc(ac, 'sine', 55), lp = filter(ac, 'lowpass', 420, 0.7), amp = gain(ac, 0);
  o.setPeriodicWave(ac.createPeriodicWave(new Float32Array(7), Float32Array.from([0, 1, 0.45, 0.2, 0.1, 0.05, 0.025]), { disableNormalization: true }));
  o.connect(lp); lp.connect(amp); amp.connect(out);
  const p = persistent(registry, tag, [o], [o, lp, amp], t0);
  let lastOff = -Infinity;
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = SPOOKY.bass * vel, off = t + Math.max(0.12, dur), legato = slide && t < lastOff + 0.06;
      hold(o.frequency, t);
      if (legato) o.frequency.setTargetAtTime(f, t, 0.03); else o.frequency.setValueAtTime(f, t);
      const g = amp.gain;
      hold(g, t);
      if (legato) g.setTargetAtTime(0.5 * A, t, 0.05);
      else {
        hold(lp.frequency, t);
        lp.frequency.setTargetAtTime(900 + 900 * vel, t, 0.003); lp.frequency.setTargetAtTime(420, t + 0.012, 0.11);
        g.setTargetAtTime(A, t, 0.005); g.setTargetAtTime(0.55 * A, t + 0.03, 0.28);
      }
      if (off > t + 0.5) g.setTargetAtTime(0, t + 0.5, 2.6);
      g.setTargetAtTime(0, off, 0.07);
      lastOff = off;
    },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- spooky kit: persistent voices sharing one noise source ---------------- */
/**
 * kit: { kickF0, kickF1, kickDecay, snapBP, snapDecay, tick, tock, lp }. Everything through the bus LP (kit.lp, Q .5); reverb
 * send .4 (snaps, knocks, the rattle), the kick and clock dry.
 * Kick (deep, round): sine kickF0 → kickF1 (τ 40 ms), .58v, attack τ 2.5 ms, decay kickDecay; a felt thud: noise → BP 180 Hz
 * (Q .8), .32v, τ 18 ms. Snap (clap lane): noise → BP snapBP (Q 1.3), 1.7v, decay snapDecay, + the finger's thud on the palm:
 * noise → BP 750 Hz, .6v, τ 12 ms; pan +.12. Clock (hat lane: tick 'x', tock 'o'): a sine ping at tick / tock Hz (.06 / .075v,
 * τ 16 / 22 ms) + a noise click → BP 4.2 / 2.6 kHz (Q 1.8), .2 / .15v, τ 3.5 / 5 ms; pan −.25. Knock (rim lane, a wooden
 * door): sine 210 → 170 Hz, .4v, τ 30 ms, + a knuckle: noise → BP 1.3k, .4v, τ 6 ms. Palm tap (snare lane, which no grid
 * uses): the snap's thud, .6v, τ 35 ms (ghost .3v, 20 ms). Rattle (shaker lane, dry bones): noise → BP
 * 3.8 kHz (Q .9), three bursts 21 ms apart (1 · .55 · .3), .32v, τ 20 ms; pan +.3.
 */
export function createSpookKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime, r = Math.random }) {
  const L = SPOOKY.kit, nodes = [];
  const N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5)), sendG = N(gain(ac, 0.4));
  bus.connect(out); sendG.connect(send);
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kBP = N(filter(ac, 'bandpass', 180, 0.8)), kTh = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); src.connect(kBP); kBP.connect(kTh); kTh.connect(bus);
  const snBP = N(filter(ac, 'bandpass', kit.snapBP, 1.3)), snA = N(gain(ac, 0)), thBP = N(filter(ac, 'bandpass', 750, 1)), thA = N(gain(ac, 0));
  const snPan = N(panner(ac, 0.12));
  src.connect(snBP); snBP.connect(snA); snA.connect(snPan); src.connect(thBP); thBP.connect(thA); thA.connect(snPan);
  snPan.connect(bus); snPan.connect(sendG);
  const cOsc = N(osc(ac, 'sine', kit.tick)), cPing = N(gain(ac, 0)), cBP = N(filter(ac, 'bandpass', 4200, 1.8)), cN = N(gain(ac, 0));
  const cPan = N(panner(ac, -0.25));
  cOsc.connect(cPing); cPing.connect(cPan); src.connect(cBP); cBP.connect(cN); cN.connect(cPan); cPan.connect(bus);
  const rOsc = N(osc(ac, 'sine', 210)), rAmp = N(gain(ac, 0)), rnBP = N(filter(ac, 'bandpass', 1300, 1)), rN = N(gain(ac, 0));
  rOsc.connect(rAmp); src.connect(rnBP); rnBP.connect(rN);
  rAmp.connect(bus); rAmp.connect(sendG); rN.connect(bus); rN.connect(sendG);
  const xBP = N(filter(ac, 'bandpass', 3800, 0.9)), xAmp = N(gain(ac, 0)), xPan = N(panner(ac, 0.3));
  src.connect(xBP); xBP.connect(xAmp); xAmp.connect(xPan); xPan.connect(bus); xPan.connect(sendG);
  const p = persistent(registry, tag, [src, kOsc, cOsc, rOsc], nodes, t0, r() * 3);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.04);
      hit(kAmp.gain, 0.58 * v * L, t, 0.0025, 0.012, kit.kickDecay);
      hit(kTh.gain, 0.32 * v * L, t, 0.001, 0.004, 0.018);
    },
    clap(t, v) {                                     // a finger snap
      hit(snA.gain, 1.7 * v * L, t, 0.0008, 0.002, kit.snapDecay);
      hit(thA.gain, 0.6 * v * L, t, 0.0005, 0.002, 0.012);
    },
    hat(t, v, open = false) {                        // the clock: tick ('x') and tock ('o')
      cOsc.frequency.setValueAtTime(open ? kit.tock : kit.tick, t);
      cBP.frequency.setValueAtTime(open ? 2600 : 4200, t);
      hit(cPing.gain, (open ? 0.075 : 0.06) * v * L, t, 0.0004, 0.001, open ? 0.022 : 0.016);
      hit(cN.gain, (open ? 0.15 : 0.2) * v * L, t, 0.0003, 0.0008, open ? 0.005 : 0.0035);
    },
    rim(t, v) {                                      // a knock on a wooden door
      rOsc.frequency.setValueAtTime(210, t); rOsc.frequency.setTargetAtTime(170, t, 0.02);
      hit(rAmp.gain, 0.4 * v * L, t, 0.0008, 0.002, 0.03);
      hit(rN.gain, 0.4 * v * L, t, 0.0004, 0.001, 0.006);
    },
    snare(t, v, ghost = false) { hit(thA.gain, (ghost ? 0.3 : 0.6) * v * L, t, 0.001, 0.003, ghost ? 0.02 : 0.035); },   // a palm tap
    shaker(t, v) { for (const [d, a] of [[0, 1], [0.021, 0.55], [0.042, 0.3]]) hit(xAmp.gain, 0.32 * v * L * a, t + d, 0.002, 0.004, 0.02); },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- lead: a theremin (or a bowed saw), mono ---------------- */
/**
 * plan.lead: { timbre 'theremin' | 'saw', pan } · plan.spooky: { vib ¢, vibRate Hz, glide τ, swoop }.
 * One oscillator (theremin: partials 1 · .09 · .045 · .02 · .01, a sine with a hollow edge; saw: 1 · .03 · .01, purer)
 * → LP 2.2 / 2.6 kHz (Q .5) → amp .2·v·SPOOKY.lead (the saw .25: its slow swell reads quieter; ×(1 − .012 per semitone
 * over MIDI 76)), attack τ 70 ms (saw 150 ms), easing to .82 (τ .8 s), release τ .22 s (saw .3 s). Portamento: a note starting within .45 s of the last one's end glides to
 * its pitch (τ glide, 60–120 ms) without a new attack (τ 90 ms to its level); a phrase's first note swoops up from 3–5
 * semitones below (p swoop, τ 130 / 200 ms), else the saw scoops up from −50 ¢ (τ 160 ms). Vibrato: theremin ±vib ¢
 * (26–42) at 4.8–5.8 Hz, a third of it at once and all of it from +.12 s (τ .25 s); saw ±18–28 ¢ at 3.8–4.6 Hz from +.3 s
 * (τ .4 s). Pan plan.lead.pan; a quarter-note echo (LP 1.8 kHz, fb .35, wet .3); reverb .5.
 */
export function createTheremin(ac, out, send, plan, { registry, tag, stepDur, t0 = ac.currentTime, r = Math.random }) {
  const S = plan.spooky, saw = plan.lead.timbre === 'saw', nodes = [];
  const N = x => (nodes.push(x), x);
  const o = N(osc(ac, 'sine', 440)), lp = N(filter(ac, 'lowpass', saw ? 2600 : 2200, 0.5)), amp = N(gain(ac, 0));
  const parts = saw ? [0, 1, 0.03, 0.01] : [0, 1, 0.09, 0.045, 0.02, 0.01];
  o.setPeriodicWave(ac.createPeriodicWave(new Float32Array(parts.length), Float32Array.from(parts), { disableNormalization: true }));
  const vib = N(osc(ac, 'sine', S.vibRate)), vibG = N(gain(ac, 0));
  vib.connect(vibG); vibG.connect(o.detune);
  const pan = N(panner(ac, plan.lead.pan || 0)), sendG = N(gain(ac, 0.5));
  o.connect(lp); lp.connect(amp); amp.connect(pan); pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 1800, 0.5)), fb = N(gain(ac, 0.35)), wet = N(gain(ac, 0.3));
  dly.delayTime.value = Math.min(1.9, 4 * stepDur);
  amp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  const p = persistent(registry, tag, [o, vib], nodes, t0);
  let lastOff = -Infinity;
  return {
    note(t, midi, vel, dur) {
      const f = mtof(midi), v = clamp(vel, 0.05, 1), off = t + Math.max(0.1, dur), legato = t < lastOff + 0.45;
      const A = (saw ? 0.25 : 0.2) * v * SPOOKY.lead * (1 - 0.012 * clamp(midi - 76, 0, 12)), q = o.frequency;
      hold(q, t);
      if (legato) q.setTargetAtTime(f, t, S.glide);
      else if (r() < S.swoop) { q.setValueAtTime(f / Math.pow(2, (3 + Math.floor(r() * 3)) / 12), t); q.setTargetAtTime(f, t + 0.02, saw ? 0.2 : 0.13); }
      else if (saw) { q.setValueAtTime(f / cents(50), t); q.setTargetAtTime(f, t + 0.01, 0.16); }
      else q.setValueAtTime(f, t);
      const g = amp.gain;
      hold(g, t);
      g.setTargetAtTime(A, t, legato ? 0.09 : saw ? 0.15 : 0.07);
      g.setTargetAtTime(0.82 * A, t + 0.35, 0.8);
      g.setTargetAtTime(0, off, saw ? 0.3 : 0.22);
      const vg = vibG.gain;
      hold(vg, t);
      if (!legato) vg.setTargetAtTime(saw ? 0 : S.vib / 3, t, 0.05);
      vg.setTargetAtTime(S.vib, t + (saw ? 0.3 : 0.12), saw ? 0.4 : 0.25);
      lastOff = off;
    },
    dispose(t) { p.dispose(t); },
  };
}
